import { ReactElement, Suspense, useEffect } from 'react';
import {
  Button,
  ContextMenu,
  ContextualTablePagination,
  FullLoader,
  Pill,
  TableColumns,
  TableContainer,
  TableRows,
  useTableContext,
} from '@sc/shared-ui';
import {
  CalendarIcon,
  MoonIcon,
  PlusIcon,
  SunIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useSuspenseQuery } from '@apollo/client/react';
import { useRouter } from '@tanstack/react-router';
import { format } from 'date-fns';
import { getFragmentData, ThemeType } from '@sc/graphql';
import {
  GQL_FRAGMENT_THEME,
  GQL_MUTATION_DELETE_THEME,
  GQL_QUERY_INDEX_THEMES,
} from '../utility/queries';
import { useAlerts } from '../providers/alerts';
import { notify } from '../utility/notify';
import { Route as BrandThemesRoute } from '../routes/_authenticated/brand-themes';
import { ThemeFormModal } from '../modals/ThemeFormModal';
import { track } from '../utility/analytics';

const themeTypeDisplay: Record<string, string> = {
  [ThemeType.dark]: 'Dark',
  [ThemeType.light]: 'Light',
};

type ThemeRowType = {
  uuid: string;
  name: string;
  createdAt: string;
  themeType: ThemeType;
  brandColour: string;
  used: boolean;
  projectCount: number;
  logo: { src: string };
};

const buildThemeRow = ({
  theme,
  onEdit,
  onDelete,
}: {
  theme: ThemeRowType;
  onEdit: (uuid: string) => void;
  onDelete: (theme: ThemeRowType) => void;
}) => ({
  uuid: theme.uuid,
  cells: [
    {
      width: 12,
      content: (
        <div className="size-10 rounded-xs bg-background-primary overflow-hidden flex items-center justify-center">
          <img
            alt={`${theme.name} logo`}
            src={theme.logo.src}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      ),
    },
    {
      width: 14,
      content: <span className="text-sm">{theme.name}</span>,
    },
    {
      width: 16,
      content: (
        <div className="flex items-center space-x-2">
          <CalendarIcon className="size-5 text-grey-400" />
          <span className="text-sm">
            {format(theme.createdAt, 'd MMMM yyyy')}
          </span>
        </div>
      ),
    },
    {
      width: 14,
      content: (
        <div className="flex items-center space-x-2">
          {theme.themeType === ThemeType.dark ? (
            <MoonIcon className="size-5 text-grey-400" />
          ) : (
            <SunIcon className="size-5 text-grey-400" />
          )}
          <span className="text-sm">{themeTypeDisplay[theme.themeType]}</span>
        </div>
      ),
    },
    {
      width: 14,
      content: (
        <div className="flex items-center space-x-2">
          <span
            aria-hidden
            className="size-5 rounded-xs border border-grey-t-700 shrink-0"
            style={{ backgroundColor: theme.brandColour }}
          />
          <span className="text-sm font-mono">{theme.brandColour}</span>
        </div>
      ),
    },
    {
      width: 10,
      content: (
        <Pill
          size="small"
          text={theme.used ? 'Used' : 'Unused'}
          colour={theme.used ? '#3EB077' : '#70808E'}
        />
      ),
    },
    {
      width: 8,
      content: <span className="text-sm">{theme.projectCount}</span>,
    },
    {
      width: 12,
      wrapperClassname: 'justify-end',
      content: (
        <div className="space-x-2 flex items-center">
          {!theme.used && (
            <ContextMenu
              size="small"
              type="basic"
              options={[{ label: 'Delete', value: 'delete' }]}
              onOptionSelect={() => onDelete(theme)}
            />
          )}
          <Button
            button={{
              text: 'Edit',
              type: 'secondary',
              size: 'small',
              shrink: true,
              onClick: () => onEdit(theme.uuid),
            }}
          />
        </div>
      ),
    },
  ],
});

const BrandThemeTableRows = ({
  onEdit,
}: {
  onEdit: (uuid: string) => void;
}) => {
  const { setTotalPages, page, rowsPerPage } = useTableContext();

  const { setAlertModal } = useAlerts();

  const { data } = useSuspenseQuery(GQL_QUERY_INDEX_THEMES, {
    variables: {
      input: {
        pagination: {
          page,
          perPage: rowsPerPage,
        },
      },
    },
    context: {
      isBatched: true,
    },
  });

  useEffect(() => {
    setTotalPages(data.indexThemes.pagination.lastPage);
  }, [data, setTotalPages]);

  const [deleteTheme] = useMutation(GQL_MUTATION_DELETE_THEME);

  const onDelete = (theme: ThemeRowType) =>
    setAlertModal({
      open: true,
      title: `Delete ${theme.name}`,
      description:
        'Are you sure you want to delete this theme? This cannot be undone.',
      icon: 'critical',
      isDelete: true,
      submitText: 'Confirm deletion',
      onSubmit: () => {
        setAlertModal(undefined);

        void deleteTheme({
          variables: { uuid: theme.uuid },
          refetchQueries: [GQL_QUERY_INDEX_THEMES],
          onCompleted: () => {
            track('Theme Deleted', { themeUuid: theme.uuid });

            notify.success('Successfully deleted theme');
          },
        });
      },
    });

  return (
    <TableRows
      widthType="pc"
      rows={data.indexThemes.items.map((item) =>
        buildThemeRow({
          theme: getFragmentData(GQL_FRAGMENT_THEME, item),
          onEdit,
          onDelete,
        })
      )}
    />
  );
};

const BrandThemeTable = ({ onEdit }: { onEdit: (uuid: string) => void }) => (
  <TableContainer>
    <TableColumns
      widthType="pc"
      columns={[
        { width: 12, heading: 'Logo' },
        { width: 14, heading: 'Name' },
        { width: 16, heading: 'Added' },
        { width: 14, heading: 'Theme type' },
        { width: 14, heading: 'Brand colour' },
        { width: 10, heading: 'State' },
        { width: 8, heading: 'Knowledge bases' },
        { width: 12 },
      ]}
    />
    <Suspense fallback={<FullLoader className="size-6" />}>
      <BrandThemeTableRows onEdit={onEdit} />
    </Suspense>
    <ContextualTablePagination />
  </TableContainer>
);

export const BrandThemes = (): ReactElement => {
  const router = useRouter();
  const search = BrandThemesRoute.useSearch();

  const mode = search?.type;
  const uuid = search?.uuid;

  const openModal = mode === 'add' || (mode === 'edit' && !!uuid);

  useEffect(() => {
    if (openModal) track('Theme Form Viewed', { mode });
  }, [mode, openModal]);

  const onClose = () =>
    void router.navigate({
      to: '/brand-themes',
      search: {},
    });

  const onEdit = (editUuid: string) =>
    void router.navigate({
      to: '/brand-themes',
      search: { type: 'edit', uuid: editUuid },
    });

  return (
    <>
      <div className="p-5 flex items-center justify-between">
        <h1 className="text-h1-small font-semibold font-grotesque">
          Brand themes
        </h1>
        <Button
          button={{
            text: 'Add theme',
            size: 'medium',
            shrink: true,
            IconEnd: PlusIcon,
            onClick: () =>
              void router.navigate({
                to: '/brand-themes',
                search: { type: 'add' },
              }),
          }}
        />
      </div>
      <div className="pb-5 flex flex-col">
        <BrandThemeTable onEdit={onEdit} />
      </div>

      <ThemeFormModal
        key={mode === 'edit' && uuid ? `edit-${uuid}` : 'add'}
        open={openModal}
        mode={mode === 'edit' ? 'edit' : 'add'}
        uuid={uuid}
        onClose={onClose}
      />
    </>
  );
};
